import type { UploadStatusResponse } from "@/features/patient/records/api/dataPipelineApi";
import type { SyncJobStatusResponse } from "@/features/patient/records/api/syncJobApi";
import type { ZipUploadEntry } from "@/features/patient/records/components/UploadHistoryList";

const SYNC_PROVIDER_LABELS: Record<string, string> = { epic: "Epic Sync" };

/** Maps one server direct-API-sync-job row to the same shape UploadHistoryList
 *  renders — the sync-job sibling of mapUploadStatusToEntry. files/
 *  parsingIssues are seeded to [] (not left undefined) specifically so
 *  UploadHistoryView's lazy-fetch effect never calls getUploadStatus against
 *  this non-upload id (see that effect's own guard). processingStage is
 *  always just "processing" while running — no per-stage weighting the way
 *  an upload's extracting/converting stages need, since every resource type
 *  fetched is an equivalent unit of work. */
export function mapSyncJobToEntry(job: SyncJobStatusResponse): ZipUploadEntry {
  return {
    id: job.sync_job_id,
    uploadId: job.sync_job_id,
    fileName: SYNC_PROVIDER_LABELS[job.provider] ?? `${job.provider} Sync`,
    uploadedAt: new Date(job.started_at).getTime(),
    status: job.status,
    errorMessage: job.error_message ?? undefined,
    files: [],
    parsingIssues: [],
    processingStage: job.status === "processing" ? "processing" : undefined,
    processingProgress: job.processing_progress ?? undefined,
    processingSeconds: job.processing_seconds ?? undefined,
    kind: "sync",
    provider: job.provider,
    // Every sync entry must open on Parsed Data — a sync has no zip/file
    // Issues to show. UploadHistoryContext's listSyncJobs effect never
    // replaces an already-known-locally entry with the one this function
    // builds (see that effect's own merge comment) — this only matters for
    // a sync job discovered fresh (e.g. a page reload mid-sync, with no
    // local addZipUpload-created entry to already carry the right tab).
    preferredTab: "structured",
  };
}

/** Maps one server upload-status row to the shape UploadHistoryList renders. */
export function mapUploadStatusToEntry(job: UploadStatusResponse): ZipUploadEntry {
  return {
    id: job.upload_id,
    uploadId: job.upload_id,
    fileName: job.file_name,
    title: job.title ?? undefined,
    uploadedAt: new Date(job.uploaded_at).getTime(),
    status: job.status,
    errorMessage: job.error_message ?? undefined,
    fileCount: job.file_count ?? undefined,
    files: job.files ?? undefined,
    parsingIssues: job.parsing_issues ?? undefined,
    processingSeconds: job.processing_seconds ?? undefined, // TEMP-TIMING
    processingStage: job.processing_stage ?? undefined,
    processingProgress: job.processing_progress ?? undefined,
    processingOcrDone: job.processing_ocr_done ?? undefined,
    processingOcrTotal: job.processing_ocr_total ?? undefined,
  };
}

/**
 * Appends one fetched page's worth of upload rows to the already-rendered
 * list, for "load more" pagination. Never drops or reorders anything
 * already in `existing` (a locally-added in-flight upload — see
 * UploadHistoryContext's addZipUpload — must survive a later page load
 * untouched) and skips any incoming row whose id is already present, so a
 * page fetched twice (e.g. a retried request) can never duplicate a row.
 *
 * Checks both `entry.id` and `entry.uploadId`, not just `entry.id`: a
 * locally-added optimistic entry keeps its client-generated `id` forever
 * (see UploadDetailPanel's own docstring on why — it's a stable React key/
 * selection identity, not a server reference) but gets `uploadId` set to
 * the server's real id once the upload response arrives. Comparing only
 * `.id` here would never recognize that a server row IS that same upload —
 * its `upload_id` matches the optimistic entry's `.uploadId`, never its
 * `.id` — and would duplicate it as a second, separate row the moment a
 * later page load happens to include it.
 */
export function appendUploadPage(
  existing: ZipUploadEntry[],
  page: UploadStatusResponse[],
): ZipUploadEntry[] {
  const existingIds = new Set(existing.flatMap((entry) => (entry.uploadId ? [entry.id, entry.uploadId] : [entry.id])));
  const appended = page
    .filter((job) => !existingIds.has(job.upload_id))
    .map(mapUploadStatusToEntry);
  return [...existing, ...appended];
}
